import { useCallback, useEffect, useRef } from 'react';

/**
 * Handlers that call `onHold` once the element has been pressed for `holdMs`
 * without being released. Mouse and touch are both handled: tablets send touch
 * events, and a touch release is followed by synthetic mouse events, which start
 * and clear a timer without firing it. The context menu a long touch opens on
 * Android is suppressed.
 *
 * Spread the result onto the element. Pass `enabled: false` to get no handlers.
 *
 * @param {Function} onHold
 * @param {{ holdMs?: number, enabled?: boolean }} [options]
 * @returns {Object} event handlers
 */
const usePressAndHold = (onHold, { holdMs = 3000, enabled = true } = {}) => {
  const timer = useRef(null);
  const onHoldRef = useRef(onHold);
  onHoldRef.current = onHold;

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const start = useCallback(() => {
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      onHoldRef.current();
    }, holdMs);
  }, [cancel, holdMs]);

  useEffect(() => {
    return cancel;
  }, [cancel]);

  if (!enabled) return {};

  return {
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onTouchCancel: cancel,
    onContextMenu: (event) => {
      event.preventDefault();
    },
  };
};

export default usePressAndHold;
